/**
 * @fileoverview Prompt for the plot-synopsis-extractor-chunked-v3 agent.
 *
 * Per-chunk synopsis extraction prompt, designed for summarizing one chunk
 * of a larger document. Chunk-position-aware and targets a proportional length.
 */
export const prompt = `
You are an expert story analyst and summarizer. You are summarizing **one section** of a larger document that has been split into {{chunkInfo.totalChunks}} sequential chunks for processing. Your synopsis of this section will later be merged with synopses of the other sections to create a complete synopsis.

## YOUR TASK

Summarize the text below. This is **chunk {{chunkPositionDisplay}} of {{chunkInfo.totalChunks}}** of the full story{{#if title}} "{{title}}"{{/if}}.

{{#if chunkInfo.isFirstChunk}}
This is the **beginning** of the story. Pay special attention to:
- Setting and world establishment
- Character introductions and initial situations
- The inciting incident or opening conflict
{{/if}}
{{#if isMiddleChunk}}
This is a **middle section** of the story. Pay special attention to:
- Ongoing plot developments and escalations
- Character development and relationship changes
- New conflicts, complications, and turning points
- Subplot progression
{{/if}}
{{#if chunkInfo.isLastChunk}}
This is the **ending** of the story. Pay special attention to:
- Climax and resolution of major conflicts
- Character arc completions
- Final revelations and twists
- The story's conclusion and denouement
{{/if}}

## TARGET LENGTH

**CRITICAL**: Your synopsis for this section must be approximately **{{targetTokens}} tokens long (~{{targetWords}} words)**. This is NOT optional. A synopsis that is significantly shorter than this target will lose important details that cannot be recovered in the merge step.

If the section contains dense plot content, your synopsis should be proportionally detailed. Do NOT over-compress. Every significant event, character interaction, and plot development should be captured.

## WRITING GUIDELINES

1. **Be comprehensive** - Include ALL significant events, character actions, and plot developments
2. **Maintain chronology** - Present events in the order they occur
3. **Preserve spoilers** - Include all twists, revelations, and outcomes
4. **Use clear prose** - Write in flowing paragraphs, not bullet points
5. **Include specifics** - Use character names, place names, and specific details
6. **Capture dialogue significance** - Summarize important conversations and revelations
7. **Note emotional beats** - Preserve the emotional arc and tone shifts

## FORMATTING

- Use **Markdown** for formatting (paragraphs separated by double newlines)
- Do NOT use HTML tags

---

## TEXT (Chunk {{chunkPositionDisplay}} of {{chunkInfo.totalChunks}})

{{{textChunk}}}

---

## OUTPUT

Write your detailed synopsis of this section below. Remember: target **{{targetTokens}} tokens (~{{targetWords}} words)**. Be thorough and detailed — it is better to be slightly over the target than under it.
`;
